import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import {
  X, Eye, Download, Send, RefreshCw, User, Car, Shield,
  Wrench, ClipboardCheck, AlertCircle, CheckCircle, FileText, Paperclip
} from 'lucide-react';

import { API_BASE_URL, getAuthHeaders as getCoreAuthHeaders } from '../../api/core.js';

function getAuthHeaders() {
  return { 'Content-Type': 'application/json', ...getCoreAuthHeaders() };
}

// Entity type configuration — labelKey resolved at render, not baked in here.
const ENTITY_TYPES = {
  driver: { labelKey: 'emails.previewModal.entityTypes.driver', icon: User, color: 'green' },
  vehicle: { labelKey: 'emails.previewModal.entityTypes.vehicle', icon: Car, color: 'purple' },
  claim: { labelKey: 'emails.previewModal.entityTypes.claim', icon: AlertCircle, color: 'red' },
  maintenance_request: { labelKey: 'emails.previewModal.entityTypes.maintenance', icon: Wrench, color: 'orange' },
  inspection: { labelKey: 'emails.previewModal.entityTypes.inspection', icon: ClipboardCheck, color: 'yellow' },
  insurance_contract: { labelKey: 'emails.previewModal.entityTypes.insurance', icon: Shield, color: 'blue' }
};

function EmailPreviewModal({ template, onClose, onMessage }) {
  const { t } = useTranslation();
  const [entityType, setEntityType] = useState('driver');
  const [entityId, setEntityId] = useState('');
  const [previewData, setPreviewData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [activeTab, setActiveTab] = useState('text'); // 'text' | 'html' | 'raw' | 'attachments'
  const [selectedAttachments, setSelectedAttachments] = useState([]);

  // Fetch entities for dropdown
  const { data: entitiesData } = useQuery({
    queryKey: ['entities', entityType],
    queryFn: async () => {
      const endpoint = entityType === 'maintenance_request' ? 'maintenance_request' : entityType;
      const res = await fetch(`${API_BASE_URL}/${endpoint}`, {
        headers: getAuthHeaders()
      });
      return res.json();
    },
    enabled: !!entityType
  });

  const entities = entitiesData?.data || [];

  // Fetch attachable documents for entity
  const { data: attachableData } = useQuery({
    queryKey: ['attachableDocuments', entityType, entityId],
    queryFn: async () => {
      const res = await fetch(`${API_BASE_URL}/email_templates/attachable/${entityType}/${entityId}`, {
        headers: getAuthHeaders()
      });
      return res.json();
    },
    enabled: !!entityId && !!entityType
  });

  const attachableDocuments = attachableData?.data || [];

  // Auto-select first entity
  useEffect(() => {
    if (entities.length > 0 && !entityId) {
      setEntityId(entities[0].id);
    }
  }, [entities, entityId]);

  const handlePreview = async () => {
    if (!entityId) {
      onMessage?.({
        title: t('common.messages.warningTitle'),
        message: t('emails.previewModal.selectEntityWarning'),
        type: 'warning'
      });
      return;
    }

    setLoading(true);
    try {
      const res = await fetch(`${API_BASE_URL}/email_templates/${template.id}/preview`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          entity_type: entityType,
          entity_id: entityId
        })
      });
      const result = await res.json();

      if (result.ok) {
        setPreviewData(result.data);
      } else {
        throw new Error(result.error?.message || t('emails.previewModal.errors.preview'));
      }
    } catch (err) {
      onMessage?.({
        title: t('common.messages.errorTitle'),
        message: err.message,
        type: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleGenerate = async () => {
    if (!entityId) {
      onMessage?.({
        title: t('common.messages.warningTitle'),
        message: t('emails.previewModal.selectEntityWarning'),
        type: 'warning'
      });
      return;
    }

    setGenerating(true);
    try {
      const res = await fetch(`${API_BASE_URL}/email_templates/${template.id}/generate`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          entity_type: entityType,
          entity_id: entityId
        })
      });
      const result = await res.json();

      if (result.ok) {
        onMessage?.({
          title: t('common.messages.successTitle'),
          message: t('emails.previewModal.generateSuccess'),
          type: 'success'
        });
      } else {
        throw new Error(result.error?.message || t('emails.previewModal.errors.generate'));
      }
    } catch (err) {
      onMessage?.({
        title: t('common.messages.errorTitle'),
        message: err.message,
        type: 'error'
      });
    } finally {
      setGenerating(false);
    }
  };

  const handleDownload = async () => {
    if (!entityId) {
      onMessage?.({
        title: t('common.messages.warningTitle'),
        message: t('emails.previewModal.selectEntityWarning'),
        type: 'warning'
      });
      return;
    }

    try {
      const url = `${API_BASE_URL}/email_templates/${template.id}/download/${entityType}/${entityId}`;
      const res = await fetch(url, {
        headers: getAuthHeaders()
      });

      if (!res.ok) {
        throw new Error(t('common.errorContexts.download'));
      }

      const blob = await res.blob();
      const downloadUrl = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = downloadUrl;
      a.download = `${template.template_code}_${entityId}.eml`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(downloadUrl);

      onMessage?.({
        title: t('common.messages.successTitle'),
        message: t('emails.previewModal.downloadSuccess'),
        type: 'success'
      });
    } catch (err) {
      onMessage?.({
        title: t('common.messages.errorTitle'),
        message: err.message,
        type: 'error'
      });
    }
  };

  const getEntityLabel = (entity) => {
    if (entityType === 'driver') {
      return `${entity.first_name} ${entity.last_name}`;
    }
    if (entityType === 'vehicle') {
      return `${entity.make} ${entity.model} - ${entity.license_plate}`;
    }
    if (entityType === 'claim') {
      return `${entity.claim_number || t('emails.previewModal.entityFallback.claim', { id: entity.id })} - ${entity.claim_type || t('common.states.notAvailable')}`;
    }
    if (entityType === 'maintenance_request') {
      return `${entity.title || t('emails.previewModal.entityFallback.maintenance', { id: entity.id })}`;
    }
    if (entityType === 'inspection') {
      return `${t('emails.previewModal.entityFallback.inspection', { id: entity.id })} - ${entity.type || t('common.states.notAvailable')}`;
    }
    if (entityType === 'insurance_contract') {
      return `${entity.contract_number || t('emails.previewModal.entityFallback.insurance', { id: entity.id })} - ${entity.insurer_name || t('common.states.notAvailable')}`;
    }
    return `#${entity.id}`;
  };

  const EntityIcon = ENTITY_TYPES[entityType]?.icon || FileText;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-4xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-500 to-blue-600 text-white px-6 py-4 rounded-t-xl">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Eye className="w-6 h-6" />
              <div>
                <h2 className="text-xl font-bold">{t('emails.previewModal.title')}</h2>
                <p className="text-blue-100 text-sm">{template.template_name}</p>
              </div>
            </div>
            <button onClick={onClose} className="text-white hover:text-blue-200">
              <X className="w-6 h-6" />
            </button>
          </div>
        </div>

        {/* Entity Selection */}
        <div className="p-4 border-b border-gray-200 bg-gray-50">
          <div className="flex flex-wrap gap-4 items-end">
            <div className="flex-1 min-w-[200px]">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('emails.previewModal.entityTypeLabel')}
              </label>
              <select
                value={entityType}
                onChange={(e) => {
                  setEntityType(e.target.value);
                  setEntityId('');
                  setPreviewData(null);
                }}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              >
                {Object.entries(ENTITY_TYPES).map(([key, config]) => (
                  <option key={key} value={key}>{t(config.labelKey)}</option>
                ))}
              </select>
            </div>
            <div className="flex-1 min-w-[250px]">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('emails.previewModal.selectEntityLabel')}
              </label>
              <select
                value={entityId}
                onChange={(e) => {
                  setEntityId(e.target.value);
                  setPreviewData(null);
                }}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              >
                <option value="">{t('emails.previewModal.choosePlaceholder')}</option>
                {entities.map(entity => (
                  <option key={entity.id} value={entity.id}>
                    {getEntityLabel(entity)}
                  </option>
                ))}
              </select>
            </div>
            <button
              onClick={handlePreview}
              disabled={!entityId || loading}
              className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 disabled:opacity-50 flex items-center gap-2"
            >
              {loading ? (
                <RefreshCw className="w-4 h-4 animate-spin" />
              ) : (
                <Eye className="w-4 h-4" />
              )}
              {t('emails.previewModal.previewButton')}
            </button>
          </div>
        </div>

        {/* Preview Content */}
        <div className="flex-1 overflow-hidden flex flex-col">
          {previewData ? (
            <>
              {/* Tabs */}
              <div className="flex border-b border-gray-200 px-4">
                <button
                  onClick={() => setActiveTab('text')}
                  className={`px-4 py-2 font-medium border-b-2 transition-colors ${
                    activeTab === 'text'
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700'
                  }`}
                >
                  {t('emails.previewModal.tabs.text')}
                </button>
                {previewData.body_html && (
                  <button
                    onClick={() => setActiveTab('html')}
                    className={`px-4 py-2 font-medium border-b-2 transition-colors ${
                      activeTab === 'html'
                        ? 'border-blue-500 text-blue-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700'
                    }`}
                  >
                    HTML
                  </button>
                )}
                <button
                  onClick={() => setActiveTab('raw')}
                  className={`px-4 py-2 font-medium border-b-2 transition-colors ${
                    activeTab === 'raw'
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700'
                  }`}
                >
                  {t('emails.previewModal.tabs.variables')}
                </button>
                <button
                  onClick={() => setActiveTab('attachments')}
                  className={`px-4 py-2 font-medium border-b-2 transition-colors flex items-center gap-2 ${
                    activeTab === 'attachments'
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700'
                  }`}
                >
                  <Paperclip className="w-4 h-4" />
                  {t('emails.previewModal.tabs.attachments')}
                  {selectedAttachments.length > 0 && (
                    <span className="bg-blue-100 text-blue-700 text-xs px-1.5 py-0.5 rounded-full">
                      {selectedAttachments.length}
                    </span>
                  )}
                </button>
              </div>

              {/* Email Preview */}
              <div className="flex-1 overflow-y-auto p-4">
                {/* Subject */}
                <div className="mb-4 p-3 bg-gray-100 rounded-lg">
                  <div className="text-xs text-gray-500 mb-1">{t('emails.previewModal.subjectLabel')}</div>
                  <div className="font-semibold text-gray-900">{previewData.subject}</div>
                </div>

                {/* Body */}
                {activeTab === 'text' && (
                  <div className="bg-white border border-gray-200 rounded-lg p-4">
                    <pre className="whitespace-pre-wrap font-sans text-gray-700">
                      {previewData.body_text}
                    </pre>
                  </div>
                )}

                {activeTab === 'html' && previewData.body_html && (
                  <div className="bg-white border border-gray-200 rounded-lg p-4">
                    <div
                      className="prose max-w-none"
                      dangerouslySetInnerHTML={{ __html: previewData.body_html }}
                    />
                  </div>
                )}

                {activeTab === 'raw' && (
                  <div className="bg-gray-900 text-green-400 rounded-lg p-4 font-mono text-sm overflow-x-auto">
                    <div className="mb-4">
                      <div className="text-gray-400 mb-1">{t('emails.previewModal.resolvedVarsComment')}</div>
                      {previewData.variables && Object.entries(previewData.variables).map(([key, value]) => (
                        <div key={key}>
                          <span className="text-yellow-400">{`{${key}}`}</span>
                          <span className="text-gray-500"> = </span>
                          <span className="text-green-400">"{value}"</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {activeTab === 'attachments' && (
                  <div className="space-y-4">
                    <div className="text-sm text-gray-600 mb-2">
                      {t('emails.previewModal.attachments.selectHint')}
                    </div>
                    {attachableDocuments.length === 0 ? (
                      <div className="text-center py-8 text-gray-500">
                        <Paperclip className="w-12 h-12 mx-auto mb-2 text-gray-300" />
                        <p>{t('emails.previewModal.attachments.none')}</p>
                        <p className="text-xs mt-1">{t('emails.previewModal.attachments.noneHint')}</p>
                      </div>
                    ) : (
                      <div className="space-y-2">
                        {attachableDocuments.map((doc, idx) => (
                          <label
                            key={`${doc.source}-${doc.id}-${idx}`}
                            className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                              selectedAttachments.some(a => a.id === doc.id && a.source === doc.source)
                                ? 'border-blue-500 bg-blue-50'
                                : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                            }`}
                          >
                            <input
                              type="checkbox"
                              checked={selectedAttachments.some(a => a.id === doc.id && a.source === doc.source)}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  setSelectedAttachments([...selectedAttachments, doc]);
                                } else {
                                  setSelectedAttachments(selectedAttachments.filter(
                                    a => !(a.id === doc.id && a.source === doc.source)
                                  ));
                                }
                              }}
                              className="rounded border-gray-300 text-blue-600"
                            />
                            <FileText className="w-5 h-5 text-gray-400" />
                            <div className="flex-1">
                              <div className="font-medium text-gray-900">{doc.filename}</div>
                              <div className="text-xs text-gray-500">
                                {t('emails.previewModal.attachments.typeSource', { type: doc.type, source: doc.source })}
                              </div>
                            </div>
                          </label>
                        ))}
                      </div>
                    )}
                    {selectedAttachments.length > 0 && (
                      <div className="mt-4 p-3 bg-blue-50 rounded-lg">
                        <div className="text-sm font-medium text-blue-700 mb-2">
                          {t('emails.previewModal.attachments.selected', { count: selectedAttachments.length })}
                        </div>
                        <div className="flex flex-wrap gap-2">
                          {selectedAttachments.map((att, idx) => (
                            <span
                              key={idx}
                              className="inline-flex items-center gap-1 px-2 py-1 bg-white rounded text-sm border"
                            >
                              <Paperclip className="w-3 h-3" />
                              {att.filename}
                              <button
                                onClick={() => setSelectedAttachments(
                                  selectedAttachments.filter(a => !(a.id === att.id && a.source === att.source))
                                )}
                                className="text-gray-400 hover:text-red-500"
                              >
                                <X className="w-3 h-3" />
                              </button>
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </>
          ) : (
            <div className="flex-1 flex items-center justify-center text-gray-500">
              <div className="text-center">
                <EntityIcon className="w-16 h-16 mx-auto mb-4 text-gray-300" />
                <p>{t('emails.previewModal.emptyState.line1')}</p>
                <p className="text-sm mt-1">{t('emails.previewModal.emptyState.line2')}</p>
              </div>
            </div>
          )}
        </div>

        {/* Footer Actions */}
        <div className="border-t border-gray-200 px-6 py-4 bg-gray-50 flex justify-between items-center">
          <div className="text-sm text-gray-500">
            {previewData && (
              <span className="flex items-center gap-1">
                <CheckCircle className="w-4 h-4 text-green-500" />
                {t('emails.previewModal.successBadge')}
              </span>
            )}
          </div>
          <div className="flex gap-3">
            <button
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100"
            >
              {t('common.actions.close')}
            </button>
            <button
              onClick={handleDownload}
              disabled={!entityId}
              className="px-4 py-2 bg-purple-500 text-white rounded-lg hover:bg-purple-600 disabled:opacity-50 flex items-center gap-2"
            >
              <Download className="w-4 h-4" />
              {t('emails.previewModal.downloadEml')}
            </button>
            <button
              onClick={handleGenerate}
              disabled={!entityId || generating}
              className="px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 disabled:opacity-50 flex items-center gap-2"
            >
              {generating ? (
                <RefreshCw className="w-4 h-4 animate-spin" />
              ) : (
                <Send className="w-4 h-4" />
              )}
              {t('emails.previewModal.generateAndSave')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default EmailPreviewModal;
